import { backendBaseUrl } from '../config';

export const APP_AUTH_UNAUTHORIZED_EVENT = 'bennie:app-auth-unauthorized';

// WI-04b: the shared X-App-Password client credential is REMOVED. Auth now rides ONE ambient
// session cookie — the WI-19 `sso_session`, httpOnly and set/cleared by the backend. The FE
// holds no credential and no session marker of its own.
//
// ⚠️ AMENDED 2026-08-29 — operator ruling R-Q15 (FULL REMOVAL of the magic-link plane, every env
// including DEV). This block used to read "ambient session cookieS ... the magic-link
// `access_session` (customer) and the WI-19 `sso_session` (SSO admin)", and the FE additionally
// kept a non-secret `bennie_grant_active` localStorage marker recording that a magic-link redeem
// had succeeded in this browser. BOTH the second cookie plane and that marker are FULL-DELETED —
// not disabled, not feature-flagged — so SSO is the only authentication plane that remains. The
// history is amended rather than dropped so a reader who meets a stale `access_session` or
// `bennie_grant_active` reference elsewhere knows it was removed deliberately.

export function notifyUnauthorized(): void {
  // A bare 401. The AppAuthGate listener recomputes its branch and reconciles identity from the
  // persisted GET /api/me; there is no local credential and no local session marker to drop here.
  //
  // ⚠️ AMENDED 2026-08-29 (R-Q15): this used to say the listener "clears the stale grant marker".
  // That marker is deleted, so the listener's remaining job is purely the identity reconcile.
  window.dispatchEvent(new CustomEvent(APP_AUTH_UNAUTHORIZED_EVENT));
}

/**
 * WI-SSO-UI — a fetch bounded by a timeout so a HUNG connection (one that is established but never
 * responds — which a plain try/catch never surfaces, since it neither resolves nor rejects) can't
 * wall a session-critical call forever. On expiry the AbortController aborts the request → the
 * caller's catch runs its fail-safe branch. Honors frontend-rules: "Read timeouts on all … never
 * wait indefinitely" / "timeout fallbacks — never spin forever". `fetchImpl` defaults to the global
 * fetch (signOut's logout POSTs); refreshIdentity passes the module-captured PRISTINE fetch.
 */
export async function fetchWithTimeout(
  input: string,
  init: RequestInit,
  timeoutMs: number,
  fetchImpl: typeof fetch = fetch,
): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetchImpl(input, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

/**
 * WI-SSO-UI — start the SSO login. Navigates to the SWA EasyAuth AAD entry, which redirects to
 * Entra and, on return, establishes the SWA session the rolesSource + POST /api/auth/session
 * exchange (AppAuthGate) consume to mint the `sso_session` cookie. `post_login_redirect_uri` lands
 * the user back on the app ROOT so the gate re-runs the exchange from a clean state.
 *
 * NOTE: `/.auth/login/aad` is a ROOT-RELATIVE path on the SWA origin — NOT `backendBaseUrl` (the
 * cross-origin API). The `/.auth/*` endpoints are served by the Static Web App that hosts this FE.
 */
export function signIn(): void {
  window.location.assign('/.auth/login/aad?post_login_redirect_uri=/');
}

/**
 * F-10 — build the `post_login_redirect_uri` for an SSO bounce so a DEEP LINK survives it.
 *
 * The auto-redirect used to hardcode `/?sso_retry=1`, so an anonymous visitor who opened a
 * deep link (`/?view=toolbox`, `/?workspaceId=ws_x`) came back from Entra on a bare root and
 * silently landed on the default Projects view — the link was discarded by the login round trip.
 * This rebuilds the return target from the CURRENT location so the round trip is transparent.
 *
 * Deliberate constraints — this value is handed to EasyAuth as a redirect destination, so an
 * open redirect here would be a far worse bug than the one it fixes:
 *   - built from `pathname` + `search` ONLY. NEVER `window.location.href` (an absolute URL would
 *     carry an authority component), and there is NO caller-supplied-URL parameter — callers may
 *     only contribute extra QUERY params, never the destination.
 *   - the FRAGMENT is dropped. ⚠️ AMENDED 2026-08-29 (R-Q15): the original reason was that "the
 *     CF-11 magic-link token travels in `#access=<token>` and must never be echoed into a redirect
 *     parameter". That token plane is DELETED and no such fragment is ever produced now. The drop
 *     is KEPT unchanged regardless, because it was never only about that token: the fragment is
 *     caller-controlled input that this return leg does not need, and echoing arbitrary
 *     caller-controlled bytes into an EasyAuth redirect parameter is the hazard. (Both call sites
 *     are additionally reachable only from the gate's `bypass` branch, which by construction
 *     carries no `signedout` marker — so no such state can ride along either.)
 *   - the path must be a SAME-ORIGIN absolute path. A protocol-relative `//evil.com`, a
 *     backslash-authority `/\evil.com`, a tab/newline-obfuscated variant, or anything that is not
 *     rooted at `/` is REFUSED — logged, and replaced by the app root.
 */
export function postLoginReturnTarget(extraParams?: Record<string, string>): string {
  // Browsers strip TAB/LF/CR from URLs before parsing, so `/<TAB>/evil.com` reaches the network as
  // the protocol-relative `//evil.com`. Strip them before classifying, never after.
  const rawPath = window.location.pathname.replace(/[\t\n\r]/g, '');
  const isSameOriginPath =
    rawPath.startsWith('/') && !rawPath.startsWith('//') && !rawPath.startsWith('/\\');
  if (!isSameOriginPath) {
    // Only reachable from a crafted URL — surface it rather than silently rewriting.
    console.error(
      `[appAuth] postLoginReturnTarget: refusing non-same-origin return path ${JSON.stringify(rawPath)} — using "/"`,
    );
  }
  // A rejected path means the whole location is crafted; drop its query too rather than carrying
  // attacker-shaped params into the return leg.
  const params = new URLSearchParams(isSameOriginPath ? window.location.search : '');
  for (const [key, value] of Object.entries(extraParams ?? {})) {
    params.set(key, value);
  }
  const query = params.toString();
  const path = isSameOriginPath ? rawPath : '/';
  return query ? `${path}?${query}` : path;
}

/**
 * TD-359 — automatic recovery from a POISONED EasyAuth token store.
 *
 * Root cause (ADR-075 residual): a browser SSO session established BEFORE the `offline_access`
 * fix has no refresh token in the SWA EasyAuth store, so EasyAuth replays the same ~1h access
 * token forever. Once it expires, every rolesSource re-resolution posts a DEAD token →
 * `AUTH_ROLES_DENY reason=token_invalid` (lifetime) → no ticket → no `sso_session` → the gate
 * redirects to `?sso_retry=1`, comes back still-anonymous, and dead-ends at "Sign-in is
 * unavailable". A silent re-login CANNOT fix it because `/.auth/login/aad` reuses the same
 * poisoned store; ONLY `/.auth/logout` evicts it. That is why the loop was self-perpetuating and
 * only a manual `/.auth/logout` (or a deploy-driven session reset) ever cleared it.
 *
 * This evicts once after a verified-anonymous return. The caller gates it with a one-shot URL
 * marker; an unavailable identity read never enters this recovery path:
 * `/.auth/logout` drops the SWA session + its token store, then lands on a fresh SSO login whose
 * return carries the retry marker so the gate can still detect a true dead-end afterward.
 */
export function recoverPoisonedSsoSession(): void {
  // The re-login return carries BOTH markers: sso_retry keeps the gate's bootstrap-splash + loop-guard
  // behavior, and sso_recovered records that eviction was already tried, so a still-anonymous return
  // after this terminates at the fail-loud screen instead of evicting again. F-10: the markers are
  // ADDED to the current location (not substituted for it) so a deep link survives this second bounce
  // too — recovery is the continuation of the same login round trip, not a fresh visit.
  const relogin = `/.auth/login/aad?post_login_redirect_uri=${encodeURIComponent(
    postLoginReturnTarget({ sso_retry: '1', sso_recovered: '1' }),
  )}`;
  window.location.assign(`/.auth/logout?post_logout_redirect_uri=${encodeURIComponent(relogin)}`);
}

/**
 * Sign out covers BOTH remaining session planes and lands on the STICKY signed-out screen (a landing
 * that does NOT auto-redirect back into sign-on):
 *   1. WI-19 SSO `sso_session`  — POST /api/auth/session/logout (httpOnly; the server clear is the
 *      ONLY way to drop it). The server OVERWRITES the cookie with a short-lived invalid value under
 *      Cache-Control: no-store so the clearing Set-Cookie actually survives the SWA/Front-Door hop — a
 *      plain past-expiry delete was being STRIPPED from the cacheable response, so sso_session outlived
 *      sign-out and /api/me kept reporting authenticated:true (the D2 stale-menu + D3 signed-out-admin
 *      -view leak, confirmed live 2026-07-02).
 *   2. the SWA EasyAuth session — /.auth/logout, landing on `/?signedout=1`.
 *
 * ⚠️ AMENDED 2026-08-29 — operator ruling R-Q15 (FULL REMOVAL of the magic-link plane, every env
 * including DEV). This used to cover **ALL THREE** planes, and its step 1 was the magic-link
 * `access_session`, cleared by `POST /api/access/logout`. That endpoint and its cookie are DELETED,
 * so the leg is REMOVED rather than left as a call that would 404 on every sign-out and log a
 * fail-loud error nobody could act on. The two legs above are renumbered from 2/3; NOTHING else about
 * their behaviour changed — in particular the SSO POST is still awaited and still fail-loud, and the
 * SWA navigation still runs even when it fails. The superseded third leg is recorded here rather than
 * silently dropped, because "sign-out clears N planes" is exactly the kind of claim a future reader
 * would otherwise have to re-derive.
 *
 * The backend POST is AWAITED and FAILS LOUD on a non-2xx (a swallowed failed clear would present a
 * "signed out" UI over a still-live session). It is best-effort in the sense that matters: its failure
 * must NOT skip the SWA logout below. The `?signedout=1` marker makes the landing distinguishable from
 * a bare visit, so AppAuthGate shows the signed-out screen and SUPPRESSES the session exchange (no
 * silent re-mint from a still-live SWA ticket) instead of auto-redirecting straight back into Entra;
 * the marker self-clears on the next bare `/` visit, so navigating to the app again re-triggers the
 * sign-on redirect.
 */
export async function signOut(): Promise<void> {
  // The POST is bounded (F2): a HUNG logout must not wall the SWA-session clear + navigation below.
  // A non-2xx is surfaced (fail-loud, core-principles) — never swallowed as success — so a failed
  // server-side clear is diagnosable rather than hidden behind a signed-out UI.
  try {
    const resp = await fetchWithTimeout(`${backendBaseUrl}/api/auth/session/logout`, {
      method: 'POST',
      credentials: 'include',
    }, 5000);
    if (!resp.ok) {
      console.error(`[appAuth] signOut: POST /api/auth/session/logout returned ${resp.status} — sso_session may not be cleared`);
    }
  } catch (err) {
    console.error('[appAuth] signOut: POST /api/auth/session/logout failed:', err);
  }

  // Clear the SWA EasyAuth session and land on the STICKY signed-out screen (?signedout=1) — NOT a bare
  // '/', which the gate would auto-redirect back into sign-on (the Entra federated session is still live,
  // so /.auth/login/aad silently re-auths). The marker is what keeps the user signed out until they act.
  window.location.assign(`/.auth/logout?post_logout_redirect_uri=${encodeURIComponent('/?signedout=1')}`);
}
